import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { MapContainer, Marker, TileLayer } from 'react-leaflet';
import L from 'leaflet';
import {
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  Clock,
  ExternalLink,
  Globe,
  Info,
  LifeBuoy,
  Loader2,
  Mail,
  MapPin,
  MoreHorizontal,
  Phone,
  Scale,
  Send,
  UserPlus,
  UserRound,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { momentoRoma, statoUfficio } from '../utils/orariUfficio';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

const MESSAGGIO_MAX = 10000;
const SEDE = { lat: 41.84076, lng: 12.46642 };
const INDIRIZZO_MAPPE = 'https://www.google.com/maps/search/?api=1&query=Viale+di+Val+Fiorita+88+00144+Roma';

const OGGETTI: { valore: string; etichetta: string; Icona: LucideIcon }[] = [
  { valore: 'informazioni-generali', etichetta: 'Informazioni Generali', Icona: Info },
  { valore: 'adesione', etichetta: 'Richiesta di Adesione', Icona: UserPlus },
  { valore: 'supporto-tecnico', etichetta: 'Supporto Tecnico', Icona: LifeBuoy },
  { valore: 'eventi', etichetta: 'Eventi e Formazione', Icona: CalendarDays },
  { valore: 'normative', etichetta: 'Questioni Normative', Icona: Scale },
  { valore: 'altro', etichetta: 'Altro', Icona: MoreHorizontal },
];

const ORARI = [
  { giorni: 'Lunedì - Giovedì', ore: '9:00-13:00 14:30-18:30' },
  { giorni: 'Venerdì', ore: '9:00-13:00 14:00-18:00' },
  { giorni: 'Sabato e Domenica', ore: 'Chiuso' },
];

const segnaposto = new L.DivIcon({
  html: `<div style="background:linear-gradient(135deg,#22c55e,#15803d);width:30px;height:30px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:3px solid #fff;box-shadow:0 4px 10px rgba(0,0,0,.35)"></div>`,
  className: '',
  iconSize: [30, 30],
  iconAnchor: [15, 30],
});

const fontCorsivo = { fontFamily: "'Caveat', cursive" };

const calcolaStato = () => {
  const { giorno, minuti } = momentoRoma(new Date());
  return statoUfficio(giorno, minuti);
};

const SchedaContatto: React.FC<{ Icona: LucideIcon; titolo: string; children: React.ReactNode }> = ({ Icona, titolo, children }) => (
  <div className="flex gap-4 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10">
    <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-primary-50 dark:bg-primary-900/40">
      <Icona className="h-5 w-5 text-primary-700 dark:text-primary-400" aria-hidden="true" />
    </div>
    <div className="min-w-0">
      <h3 className="text-base font-semibold text-gray-900 dark:text-white">{titolo}</h3>
      <div className="mt-1 text-sm leading-relaxed text-gray-600 dark:text-gray-300">{children}</div>
    </div>
  </div>
);

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    website: '', // honeypot anti-spam: nascosto agli utenti, i bot lo compilano
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [ticketNumber, setTicketNumber] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [statoSede, setStatoSede] = useState(calcolaStato);

  // L'etichetta "Aperto ora / Chiuso" si aggiorna da sola ogni minuto
  useEffect(() => {
    const timer = setInterval(() => setStatoSede(calcolaStato()), 60000);
    return () => clearInterval(timer);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage(null);
    setTicketNumber(null);

    try {
      const response = await fetch(`${API_BASE_URL}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitStatus('success');
        setTicketNumber(data.data?.ticketNumber || null);
        setFormData({ name: '', email: '', subject: '', message: '', website: '' });
      } else {
        setSubmitStatus('error');
        setErrorMessage(
          response.status === 429
            ? 'Hai inviato troppi messaggi in poco tempo. Riprova tra qualche minuto.'
            : null
        );
        console.error('Errore invio form:', data.message);
      }
    } catch (error) {
      setSubmitStatus('error');
      console.error('Errore invio form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <Helmet>
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link href="https://fonts.googleapis.com/css2?family=Caveat:wght@600&display=swap" rel="stylesheet" />
      </Helmet>

      {/* Testata */}
      <section className="relative overflow-hidden bg-primary-950">
        <div className="absolute inset-y-0 right-0 w-full lg:w-[60%]">
          <img
            src="/images/contatti/sportello.webp"
            srcSet="/images/contatti/sportello-1280.webp 1280w, /images/contatti/sportello.webp 2400w"
            sizes="(min-width: 1024px) 60vw, 100vw"
            alt=""
            aria-hidden="true"
            fetchPriority="high"
            className="h-full w-full object-cover object-[60%_center]"
          />
        </div>
        <div className="absolute inset-0 bg-primary-950/80 lg:bg-transparent lg:bg-gradient-to-r lg:from-primary-950 lg:from-[40%] lg:via-primary-950/70 lg:via-[55%] lg:to-transparent" />

        <div className="relative container-custom flex min-h-[26rem] items-center pt-16 pb-36 md:pt-20 md:pb-40">
          <div className="max-w-xl motion-safe:animate-fade-in">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-primary-300">Parliamone</p>
            <h1 className="mb-5 text-4xl md:text-6xl font-bold tracking-tight text-white">
              Contattaci
            </h1>
            <p className="text-base md:text-lg leading-relaxed text-white/80">
              Questa pagina è dedicata a chi non è ancora associato: informazioni, adesione, collaborazioni.
            </p>
          </div>
        </div>

        <div className="pointer-events-none absolute bottom-40 right-10 hidden -rotate-6 text-right text-white lg:block xl:right-16" aria-hidden="true">
          <span className="block text-4xl leading-tight drop-shadow-lg" style={fontCorsivo}>
            Siamo qui<br />per ascoltarti
          </span>
          <svg viewBox="0 0 220 16" className="ml-auto mt-1 w-52 text-primary-400" fill="none">
            <path d="M3 12 C 60 2, 140 2, 217 8" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
          </svg>
        </div>
      </section>

      <div className="container-custom relative -mt-24 pb-20">
        {/* Gli associati scrivono spesso da qui: li si indirizza al supporto dell'area
            riservata, dove il ticket resta collegato allo studio e alle sue pratiche. */}
        <section
          aria-labelledby="contact-associati-title"
          className="relative mb-12 overflow-hidden rounded-2xl bg-white p-6 shadow-2xl ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10 sm:p-8"
        >
          <div className="absolute inset-y-0 left-0 w-1.5 bg-gradient-to-b from-primary-400 to-primary-700" aria-hidden="true" />
          <div className="flex flex-col gap-6 md:flex-row md:items-center">
            <div className="flex flex-1 items-start gap-4">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-primary-600 text-white shadow">
                <UserRound className="h-6 w-6" aria-hidden="true" />
              </div>
              <div>
                <h2 id="contact-associati-title" className="text-lg font-semibold text-gray-900 dark:text-white">
                  Sei un associato UNASCA? Non usare questo modulo.
                </h2>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                  Per svincolo atti, rimborso voucher, Rete Certificata, area riservata e ogni altra richiesta
                  legata al tuo studio usa il <strong className="text-gray-900 dark:text-white">Supporto dell'area riservata</strong>: la richiesta arriva
                  subito al gruppo giusto, resta collegata alla tua anagrafica e ne segui le risposte online.
                </p>
              </div>
            </div>
            <div className="flex flex-shrink-0 flex-col gap-2 sm:flex-row md:flex-col md:items-stretch">
              <a href="/area-riservata/supporto" className="btn btn-primary group whitespace-nowrap text-center">
                Vai al supporto associati
                <ArrowRight className="ml-2 h-4 w-4 transition-transform motion-safe:group-hover:translate-x-1" aria-hidden="true" />
              </a>
              <span className="text-center text-xs text-gray-500 dark:text-gray-400">
                Accesso con Partita IVA o email dello studio
              </span>
            </div>
          </div>
        </section>

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          {/* Informazioni di contatto */}
          <div className="lg:col-span-5">
            <h2 className="mb-6 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
              Informazioni di Contatto
            </h2>

            <div className="mb-4 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10">
              <div className="relative z-0 h-52">
                <MapContainer
                  center={[SEDE.lat, SEDE.lng]}
                  zoom={16}
                  scrollWheelZoom={false}
                  style={{ height: '100%', width: '100%' }}
                  attributionControl
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <Marker position={[SEDE.lat, SEDE.lng]} icon={segnaposto} />
                </MapContainer>
              </div>
              <div className="flex items-start gap-4 p-5">
                <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-primary-50 dark:bg-primary-900/40">
                  <MapPin className="h-5 w-5 text-primary-700 dark:text-primary-400" aria-hidden="true" />
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">Sede Legale</h3>
                  <p className="mt-1 text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                    UNASCA <br />
                    Viale di Val Fiorita, 88 – int.11<br />
                    00144 ROMA<br />
                    Tel. 06.54.22.19.86
                  </p>
                  <a
                    href={INDIRIZZO_MAPPE}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 hover:underline underline-offset-4 dark:text-primary-400"
                  >
                    Apri in Maps
                    <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                  </a>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="relative overflow-hidden rounded-2xl bg-primary-900 shadow-sm">
                <img
                  src="/images/contatti/telefono.webp"
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover object-[45%_center] opacity-60"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/60 to-primary-950/10" />
                <div className="relative flex items-center justify-between gap-4 p-5">
                  <div>
                    <h3 className="text-sm font-medium text-white/75">Telefono</h3>
                    <a
                      href="tel:+390654221986"
                      className="mt-0.5 block text-2xl font-bold tracking-tight text-white hover:underline underline-offset-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-white rounded"
                    >
                      06.54.22.19.86
                    </a>
                  </div>
                  <a
                    href="tel:+390654221986"
                    aria-label="Chiama la sede UNASCA"
                    className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-white text-primary-800 shadow-lg transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary-900"
                  >
                    <Phone className="h-5 w-5" aria-hidden="true" />
                  </a>
                </div>
              </div>

              <SchedaContatto Icona={Mail} titolo="Email">
                <a href="mailto:info@studi.unasca.it" className="font-medium text-primary-700 hover:underline underline-offset-4 dark:text-primary-400">
                  info@studi.unasca.it
                </a>
                <br />
                <span className="text-xs text-gray-500 dark:text-gray-400">Mail ufficiale</span>
              </SchedaContatto>

              <SchedaContatto Icona={Globe} titolo="Sito Web Ufficiale">
                <a
                  href="https://www.unasca.it"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-primary-700 hover:underline underline-offset-4 dark:text-primary-400"
                >
                  www.unasca.it
                </a>
              </SchedaContatto>

              <SchedaContatto Icona={Clock} titolo="Orari di Ufficio">
                <span
                  className={`mb-2 inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-semibold ${
                    statoSede.aperto
                      ? 'bg-primary-50 text-primary-800 dark:bg-primary-900/40 dark:text-primary-300'
                      : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                  }`}
                >
                  <span className="relative flex h-2 w-2" aria-hidden="true">
                    {statoSede.aperto && (
                      <span className="absolute inline-flex h-full w-full rounded-full bg-primary-500 opacity-75 motion-safe:animate-ping" />
                    )}
                    <span className={`relative inline-flex h-2 w-2 rounded-full ${statoSede.aperto ? 'bg-primary-500' : 'bg-gray-400'}`} />
                  </span>
                  {statoSede.testo}
                </span>
                <dl className="space-y-0.5">
                  {ORARI.map(({ giorni, ore }) => (
                    <div key={giorni} className="flex flex-wrap gap-x-2">
                      <dt>{giorni}:</dt>
                      <dd className="font-medium text-gray-800 dark:text-gray-200">{ore}</dd>
                    </div>
                  ))}
                </dl>
              </SchedaContatto>
            </div>
          </div>

          {/* Modulo */}
          <div className="lg:col-span-7">
            <div className="rounded-3xl bg-white p-6 shadow-xl ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10 sm:p-8 lg:p-10">
              {submitStatus === 'success' ? (
                <div className="flex flex-col items-center py-10 text-center motion-safe:animate-fade-in" role="status">
                  <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-primary-50 ring-8 ring-primary-50/50 dark:bg-primary-900/40 dark:ring-primary-900/20">
                    <CheckCircle2 className="h-10 w-10 text-primary-600 dark:text-primary-400" aria-hidden="true" />
                  </div>
                  <h2 className="mb-3 text-2xl font-bold text-gray-900 dark:text-white">
                    {ticketNumber ? `Richiesta #${ticketNumber} registrata` : 'Messaggio inviato'}
                  </h2>
                  <p className="max-w-md text-base text-gray-600 dark:text-gray-300">
                    {ticketNumber
                      ? `Richiesta #${ticketNumber} registrata: appena sarà affidata all'ufficio competente riceverai un'email con il link per seguirla.`
                      : 'Messaggio inviato con successo! Ti risponderemo al più presto.'}
                  </p>
                  <button
                    type="button"
                    onClick={() => setSubmitStatus('idle')}
                    className="btn btn-secondary mt-8"
                  >
                    Scrivi un altro messaggio
                  </button>
                </div>
              ) : (
                <>
                  <h2 className="mb-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                    Invia un Messaggio
                  </h2>
                  <p className="mb-8 text-sm text-gray-500 dark:text-gray-400">
                    Modulo per chi non è associato. Se sei associato,{' '}
                    <a href="/area-riservata/supporto" className="font-medium text-primary-600 hover:underline dark:text-primary-400">
                      apri una richiesta dall'area riservata
                    </a>.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    {submitStatus === 'error' && (
                      <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                        {errorMessage || "Errore nell'invio del messaggio. Riprova più tardi."}
                      </div>
                    )}

                    {/* Honeypot: invisibile agli utenti, compilato dai bot */}
                    <input
                      type="text"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      tabIndex={-1}
                      autoComplete="off"
                      className="hidden"
                      aria-hidden="true"
                    />

                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                      <div>
                        <label htmlFor="name" className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Nome *
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          autoComplete="name"
                          className="input"
                          value={formData.name}
                          onChange={handleChange}
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Email *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          autoComplete="email"
                          className="input"
                          value={formData.email}
                          onChange={handleChange}
                        />
                      </div>
                    </div>

                    <fieldset>
                      <legend className="mb-3 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Oggetto *
                      </legend>
                      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                        {OGGETTI.map(({ valore, etichetta, Icona }, i) => {
                          const scelto = formData.subject === valore;
                          return (
                            <label key={valore} className="cursor-pointer">
                              <input
                                type="radio"
                                name="subject"
                                value={valore}
                                required={i === 0}
                                checked={scelto}
                                onChange={handleChange}
                                className="peer sr-only"
                              />
                              <span
                                className={`flex h-full flex-col items-start gap-2 rounded-xl border p-3 text-sm transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-primary-500 peer-focus-visible:ring-offset-2 dark:peer-focus-visible:ring-offset-gray-900 ${
                                  scelto
                                    ? 'border-primary-600 bg-primary-50 text-primary-900 dark:border-primary-500 dark:bg-primary-900/30 dark:text-primary-100'
                                    : 'border-gray-200 text-gray-700 hover:border-primary-300 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:border-primary-700 dark:hover:bg-gray-800'
                                }`}
                              >
                                <Icona
                                  className={`h-5 w-5 ${scelto ? 'text-primary-700 dark:text-primary-300' : 'text-gray-400'}`}
                                  aria-hidden="true"
                                />
                                <span className="font-medium leading-tight">{etichetta}</span>
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </fieldset>

                    <div>
                      <div className="mb-2 flex items-baseline justify-between">
                        <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Messaggio *
                        </label>
                        <span className="text-xs tabular-nums text-gray-400" aria-live="polite">
                          {formData.message.length.toLocaleString('it-IT')} / {MESSAGGIO_MAX.toLocaleString('it-IT')}
                        </span>
                      </div>
                      <textarea
                        id="message"
                        name="message"
                        rows={6}
                        required
                        maxLength={MESSAGGIO_MAX}
                        className="input"
                        placeholder="Scrivi qui il tuo messaggio..."
                        value={formData.message}
                        onChange={handleChange}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn btn-primary btn-large group w-full disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="-ml-1 mr-3 h-5 w-5 animate-spin" aria-hidden="true" />
                          Invio in corso...
                        </>
                      ) : (
                        <>
                          Invia Messaggio
                          <Send className="ml-2 h-5 w-5 transition-transform motion-safe:group-hover:translate-x-1" aria-hidden="true" />
                        </>
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
